export function toBinary(num: number): string {
  return num.toString(2)
}

export function toOctal(num: number): string {
  return num.toString(8)
}

export function toHex(num: number): string {
  return num.toString(16).toUpperCase()
}

export function fromBinary(str: string): number {
  return parseInt(str, 2)
}

export function fromOctal(str: string): number {
  return parseInt(str, 8)
}

export function fromHex(str: string): number {
  return parseInt(str, 16)
}

export function toBase(num: number, base: number): string {
  if (base < 2 || base > 36) {
    throw new Error('Base must be between 2 and 36')
  }
  return num.toString(base).toUpperCase()
}

export function fromBase(str: string, base: number): number {
  if (base < 2 || base > 36) {
    throw new Error('Base must be between 2 and 36')
  }
  return parseInt(str, base)
}

export function toRoman(num: number): string {
  if (num < 1 || num > 3999) {
    throw new Error('Number must be between 1 and 3999')
  }

  const romanNumerals: [number, string][] = [
    [1000, 'M'],
    [900, 'CM'],
    [500, 'D'],
    [400, 'CD'],
    [100, 'C'],
    [90, 'XC'],
    [50, 'L'],
    [40, 'XL'],
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
  ]

  let result = ''
  for (const [value, symbol] of romanNumerals) {
    while (num >= value) {
      result += symbol
      num -= value
    }
  }
  return result
}

export function fromRoman(roman: string): number {
  const romanMap: Record<string, number> = {
    I: 1,
    V: 5,
    X: 10,
    L: 50,
    C: 100,
    D: 500,
    M: 1000,
  }

  let result = 0
  for (let i = 0; i < roman.length; i++) {
    const current = romanMap[roman[i]]
    const next = romanMap[roman[i + 1]]
    if (next && current < next) {
      result -= current
    } else {
      result += current
    }
  }
  return result
}

export function toChinese(num: number): string {
  const digits = ['零', '一', '二', '三', '四', '五', '六', '七', '八', '九']
  const units = ['', '十', '百', '千', '万', '十', '百', '千', '亿']

  if (num === 0) return '零'

  let result = ''
  const str = num.toString()
  const len = str.length

  for (let i = 0; i < len; i++) {
    const digit = parseInt(str[i])
    const unit = units[len - i - 1]

    if (digit !== 0) {
      result += digits[digit] + unit
    } else if (i > 0 && str[i - 1] !== '0') {
      result += digits[digit]
    }

    if (unit === '万' || unit === '亿') {
      result += unit
    }
  }

  return result.replace(/零+/g, '零').replace(/零$/, '')
}

export function toScientific(num: number): string {
  return num.toExponential()
}

export function fromScientific(str: string): number {
  return parseFloat(str)
}

export function toPercentage(num: number, decimals: number = 2): string {
  return (num * 100).toFixed(decimals) + '%'
}

export function toCurrency(num: number, currency: string = '¥', decimals: number = 2): string {
  return currency + num.toFixed(decimals)
}

export function formatNumber(num: number, separator: string = ','): string {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator)
}

export function padNumber(num: number, length: number, padChar: string = '0'): string {
  return num.toString().padStart(length, padChar)
}

export function toBytes(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  let unitIndex = 0
  while (bytes >= 1024 && unitIndex < units.length - 1) {
    bytes /= 1024
    unitIndex++
  }
  return bytes.toFixed(2) + units[unitIndex]
}

export function toDegrees(radians: number): number {
  return radians * (180 / Math.PI)
}

export function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}
